// PLAN-cockpit-v2-explore Phase 2: V2 検索 state + data fetching
import { useEffect, useState } from 'react'
import { apiFetch } from '../../api/client'
import type { SearchHit, SearchResponse } from '../../types'

export type Mode = 'hybrid' | 'fts' | 'embedding'

export interface V2SearchFilters {
  q: string
  mode: Mode
  origin?: 'human' | 'auto' | 'synthesized'
  subjectivity?: 'objective' | 'interpreted' | 'subjective'
  domain?: string
  domain_mode?: 'strict' | 'broad' | 'exploratory'
  source_project?: string
  since?: string
  until?: string
  sort?: 'score' | '-created_at' | 'created_at'
  limit?: number
}

const DEFAULT: V2SearchFilters = {
  q: '',
  mode: 'hybrid',
  domain_mode: 'broad',
  sort: 'score',
  limit: 50,
}

const STORAGE_RECENT = 'cockpit_v2.recent_queries'

function loadRecent(): string[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_RECENT)
    if (!raw) return []
    return JSON.parse(raw) as string[]
  } catch {
    return []
  }
}

function saveRecent(q: string) {
  if (!q) return
  const cur = loadRecent()
  const next = [q, ...cur.filter((x) => x !== q)].slice(0, 100)
  try {
    window.localStorage.setItem(STORAGE_RECENT, JSON.stringify(next))
  } catch {
    /* ignore */
  }
}

export function useV2SearchState() {
  const [filters, setFilters] = useState<V2SearchFilters>(DEFAULT)
  const update = (patch: Partial<V2SearchFilters>) => setFilters((p) => ({ ...p, ...patch }))
  const reset = () => setFilters(DEFAULT)
  return { filters, update, reset, recent: loadRecent(), pushRecent: saveRecent }
}

export interface SearchState {
  results: SearchHit[]
  total: number
  loading: boolean
  error: string | null
  latencyMs: number | null
}

export function useV2SearchResults(filters: V2SearchFilters): SearchState {
  const [state, setState] = useState<SearchState>({
    results: [],
    total: 0,
    loading: false,
    error: null,
    latencyMs: null,
  })

  useEffect(() => {
    const ac = new AbortController()
    const startedAt = Date.now()

    // PLAN-domain-classifier-iter2 (2026-05-11): q 空でも sort=-created_at で全件取得を許可。
    // worker-entry の v1 制約「q + sort != score」を守るため、q が空のときは sort=score を強制しない呼び出し側 (Explore Lens) に依存する。

    setState((p) => ({ ...p, loading: true, error: null }))

    const path =
      filters.origin === 'synthesized' ? '/api/articles/search'
      : filters.origin === 'human' || filters.origin === 'auto' ? '/api/entries/search'
      : '/api/search'

    const query: Record<string, string | number | undefined> = {
      q: filters.q || undefined,
      mode: filters.mode,
      origin: filters.origin && filters.origin !== 'synthesized' ? filters.origin : undefined,
      subjectivity: filters.subjectivity,
      domain: filters.domain,
      domain_mode: filters.domain_mode,
      source_project: filters.source_project,
      since: filters.since,
      until: filters.until,
      sort: filters.sort,
      limit: filters.limit ?? 50,
    }

    apiFetch<SearchResponse>(path, { query, signal: ac.signal })
      .then((res) => {
        setState({
          results: res.entries ?? [],
          total: res.total ?? 0,
          loading: false,
          error: null,
          latencyMs: Date.now() - startedAt,
        })
      })
      .catch((e: unknown) => {
        if ((e as { name?: string }).name === 'AbortError') return
        setState({
          results: [],
          total: 0,
          loading: false,
          error: e instanceof Error ? e.message : 'Search failed',
          latencyMs: null,
        })
      })

    return () => ac.abort()
  }, [filters.q, filters.mode, filters.origin, filters.subjectivity, filters.domain, filters.domain_mode, filters.source_project, filters.since, filters.until, filters.sort, filters.limit])

  return state
}
